// Nutrition Progress — insights engine.
// 14 rules, prioritized, deduplicated, capped at 5. Each rule function returns
// an insight object { priority, variant, text } or null when the rule doesn't fire.
// variant = "info" | "warn" | "good"

import moment from "moment";
import { dayAdherence, macroAdhPct } from "./adherence";
import { convertUnit } from "../converters/convert-units";

const fmtUtc = (utcNum) => moment(String(utcNum), "YYYYMMDD").format("MMM D");

// Re-implemented here rather than importing viewTokens.js's toDisplayWeight —
// this engine module stays view-layer-independent (no React/component-tree
// imports), same boundary the rest of utils/nutrition-progress/ already
// holds to. Identical rounding/conversion behavior, just via the shared
// convertUnit primitive both versions are built on.
const toDisplayWeight = (kgValue, isImperial) => {
  if (kgValue == null) return null;
  const converted = isImperial ? convertUnit(kgValue, "kg", "lb") : kgValue;
  return Math.round(converted * 10) / 10;
};

// Weekly rates keep two decimals (toDisplayWeight keeps one), so the pace in
// the text matches the 0.18 kg/wk the Bodyweight trend card shows.
const toDisplayRate = (kgPerWeek, isImperial) => {
  if (kgPerWeek == null) return null;
  const converted = isImperial ? convertUnit(kgPerWeek, "kg", "lb") : kgPerWeek;
  return Math.round(converted * 100) / 100;
};

// Returns the signed weight delta in the appropriate units (kg or lb)
const signedWeightDelta = (deltaKg, isImperial) => {
  const rounded = toDisplayWeight(deltaKg, isImperial);
  if (rounded == null) return "—";
  const sign = rounded >= 0 ? "+" : "";
  return `${sign}${rounded} ${isImperial ? "lb" : "kg"}`;
};

// Builds a fully-qualified key under nutritionPage.progress.insights.
const tk = (key) => `nutritionPage.progress.insights.${key}`;

/* ---- private helpers ------------------------------------------------- */

// Average kcal target across all logged days (used by intakeVsWeight rule).
const computeAvgTarget = (dayRows) => {
  const valid = dayRows.filter((d) => d.logged && d.consumed && d.target);
  return valid.length
    ? Math.round(valid.reduce((s, d) => s + d.target.kcal, 0) / valid.length)
    : null;
};

// Returns { count, changes[] } when the period spans >1 plan, null otherwise.
// changes[] = utcDates where the active plan switched.
const computePlanChangeInfo = (dayRows) => {
  const days = dayRows.slice().reverse(); // oldest-first
  const planIds = new Set(days.filter((d) => d.planId).map((d) => d.planId));
  if (planIds.size <= 1) return null;
  const changes = [];
  for (let i = 1; i < days.length; i++) {
    if (days[i].planId && days[i - 1].planId && days[i].planId !== days[i - 1].planId) {
      changes.push(days[i].utcDate);
    }
  }
  return { count: planIds.size, changes };
};

// Per-macro adherence stats sorted worst-first.
// Each entry: { labelKey (i18n suffix), adh%, dir, consistency }.
// consistency = share of logged days where the macro went in the dominant direction.
const computeMacroAdhStats = (dayRows) => {
  const valid = dayRows.filter((d) => d.logged && d.consumed && d.target);
  if (!valid.length) return null;
  const macros = [
    { key: "proteins", labelKey: "macroProtein" },
    { key: "carbs",    labelKey: "macroCarbs" },
    { key: "fats",     labelKey: "macroFat" },
    { key: "kcal",     labelKey: "macroCalories" },
  ];
  return macros
    .map(({ key, labelKey }) => {
      const adhs = valid.map((d) => macroAdhPct(d, key)).filter((v) => v != null);
      if (!adhs.length) return null;
      const avg = Math.round(adhs.reduce((s, v) => s + v, 0) / adhs.length);
      const overCount = valid.filter((d) => d.consumed[key] > d.target[key]).length;
      const overShare = overCount / valid.length;
      return {
        labelKey,
        adh: avg,
        dir: overShare >= 0.5 ? "over" : "under",
        consistency: Math.max(overShare, 1 - overShare),
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.adh - b.adh); // worst first
};

// Returns { worst: { wd, adh% }, overall } — the weekday with the lowest mean adherence
// plus the period-wide mean. Returns null when no logged days exist.
const computeWeekdayAdhStats = (dayRows) => {
  const valid = dayRows.filter((d) => d.logged && d.consumed && d.target);
  if (!valid.length) return null;
  const byWd = Array.from({ length: 7 }, () => []);
  valid.forEach((d) => {
    const wd = moment(String(d.utcDate), "YYYYMMDD").isoWeekday() - 1; // 0=Mon
    const adh = dayAdherence(d);
    if (adh != null) byWd[wd].push(adh);
  });
  const overall = Math.round(
    byWd.flat().reduce((s, v) => s + v, 0) / byWd.flat().length
  );
  const avgs = byWd
    .map((arr, i) =>
      arr.length ? { wd: i, adh: Math.round(arr.reduce((s, v) => s + v, 0) / arr.length) } : null
    )
    .filter(Boolean);
  const worst = avgs.toSorted((a, b) => a.adh - b.adh)[0];
  return worst ? { worst, overall } : null;
};

// Returns the training phase with the lowest average kcal intake, with its deficit vs target.
// Used to flag recovery risk in hard blocks. Returns null when no planner or no logged days in any span.
const computePhaseIntakeLowest = (dayRows, phaseSpans) => {
  if (!phaseSpans.length) return null;
  const results = phaseSpans
    .map((span) => {
      const spanDays = dayRows.filter(
        (d) => d.utcDate >= span.startUtc && d.utcDate <= span.endUtc && d.logged && d.consumed
      );
      if (!spanDays.length) return null;
      const avgIntake = Math.round(
        spanDays.reduce((s, d) => s + d.consumed.kcal, 0) / spanDays.length
      );
      const withTarget = spanDays.filter((d) => d.target);
      const avgTarget = withTarget.length
        ? Math.round(withTarget.reduce((s, d) => s + d.target.kcal, 0) / withTarget.length)
        : null;
      const deficit = avgTarget == null ? null : Math.round(avgTarget - avgIntake);
      return { name: span.name, avgIntake, deficit };
    })
    .filter(Boolean);
  return results.sort((a, b) => a.avgIntake - b.avgIntake)[0] ?? null;
};

// Returns the date range of menstrual cycle days in the period, or null if none.
// Requires consent-gated period data; absent when not shared.
const computeCycleWeek = (dayRows) => {
  const period = dayRows.filter((d) => d.period);
  if (!period.length) return null;
  const sorted = period.slice().sort((a, b) => a.utcDate - b.utcDate);
  return { start: sorted[0].utcDate, end: sorted[sorted.length - 1].utcDate };
};

/* ---- rules factory --------------------------------------------------- */

// Captures t + locale + isImperial once; all rule functions are clean closures
// with only their data args. locale is the BCP 47 tag (e.g. "en", "es") used
// for number formatting; isImperial is the coach's unit preference, used by
// any rule whose text carries a weight value/rate (intakeVsWeight, goal).
const makeRules = (t, locale, isImperial) => {
  const formatNumber  = (n) => n.toLocaleString(locale);
  const weekdayName   = (i) => t(tk(`weekday_${i}`));
  const macroLabel    = (labelKey) => t(tk(labelKey));

  // Rule 1 — context: period spans multiple plans; warn that targets shift mid-period.
  const multiPlan = (planChange) => {
    if (!planChange) return null;
    return {
      priority: 1, variant: "info",
      icon: "/img/new-icons/activity-line.svg",
      text: t(tk("multiPlan"), { count: planChange.count, dates: planChange.changes.map(fmtUtc).join(", ") }),
    };
  };

  // Rule 2 — context: logging rate < 60%; all downstream figures are unreliable.
  const lowLogging = (stats) => {
    if (stats.loggingRate >= 60) return null;
    return {
      priority: 1, variant: "warn",
      text: t(tk("lowLogging"), { rate: stats.loggingRate }),
    };
  };

  // Rule 3 — problem: training vs rest adherence diverge by ≥6 pts.
  // Skips when no rest targets are set (single-target plans).
  const trainingRestGap = (stats) => {
    if (!stats.hasRestData || stats.trainingAdh == null || stats.restAdh == null) return null;
    const gap = stats.trainingAdh - stats.restAdh;
    if (Math.abs(gap) < 6) return null;
    const lowAdh  = gap > 0 ? stats.restAdh    : stats.trainingAdh;
    const highAdh = gap > 0 ? stats.trainingAdh : stats.restAdh;
    const key     = gap > 0 ? "trainingRestGapRestLow" : "trainingRestGapTrainingLow";
    return {
      priority: 2, variant: "warn",
      text: t(tk(key), { lowAdh, highAdh, gap: Math.abs(gap) }),
    };
  };

  // Rule 4 — problem: the worst-performing macro averages < 85% adherence.
  // Adds directional note ("usually over/under") when ≥70% of days go the same way.
  const weakestMacro = (macroStats) => {
    if (!macroStats?.length) return null;
    const worst = macroStats[0];
    if (worst.adh >= 85) return null;
    const label = macroLabel(worst.labelKey);
    if (worst.consistency >= 0.7) {
      const dir = t(tk(worst.dir === "over" ? "dirOver" : "dirUnder"));
      return {
        priority: 3, variant: "warn",
        text: t(tk("weakestMacroWithDir"), { label, adh: worst.adh, dir }),
      };
    }
    return {
      priority: 3, variant: "warn",
      text: t(tk("weakestMacro"), { label, adh: worst.adh }),
    };
  };

  // Rule 5 — problem: the worst weekday trails the period average by ≥8 pts.
  const weekdayPattern = (wdStats) => {
    if (!wdStats) return null;
    if (wdStats.overall - wdStats.worst.adh < 8) return null;
    return {
      priority: 3, variant: "warn",
      text: t(tk("weekdayPattern"), { weekday: weekdayName(wdStats.worst.wd), adh: wdStats.worst.adh, overall: wdStats.overall }),
    };
  };

  // Rules 6 & 7 — always fires one branch (punchline):
  //   6 (incoherent): intake well below target but weight flat → likely under-logging.
  //   7 (tracking):   intake vs target and weight change are coherent → positive signal.
  //   (no weight):    zero weigh-ins logged anywhere in the period → there's no delta
  //     to report at all, not merely an unknown one. Without this branch,
  //     signedWeightDelta's "—" placeholder lands right next to intakeTracking's own
  //     literal " — " separator, rendering as a visible double-dash ("moved — —").
  const intakeVsWeight = (stats, avgTarget) => {
    if (avgTarget == null || stats.avgIntake == null) return [];
    const intake = formatNumber(stats.avgIntake);
    const target = formatNumber(avgTarget);
    if (stats.weightDelta == null) {
      return [{
        priority: 2,
        variant:  "info",
        icon:     "/img/new-icons/trending-down.svg",
        text:     t(tk("intakeNoWeight"), { intake, target }),
      }];
    }
    const isIncoherent = stats.avgIntake < avgTarget - 50 && stats.weightDelta >= -0.2;
    const params = { intake, target, delta: signedWeightDelta(stats.weightDelta, isImperial) };
    return [{
      priority: 2,
      variant:  isIncoherent ? "warn" : "info",
      icon:     "/img/new-icons/trending-down.svg",
      text:     t(tk(isIncoherent ? "intakeIncoherent" : "intakeTracking"), params),
    }];
  };

  // Rules 8-10: goal status. goalData arrives pre-computed from SummaryView.
  // (projection already resolved — no regression needed here). unit/targetWeight/
  // rate/reqRate all follow the coach's isImperial preference now — the i18n
  // strings take a {{unit}} param instead of hardcoding "kg".
  const goal = (goalData) => {
    if (!goalData) return [];
    const { targetWeight: rawTargetWeight, ratePerWeek, movingToward, weeksToReach, weeksToTarget, reqRate: rawReqRate, extraDeficit, paceState } = goalData;
    if (ratePerWeek == null || weeksToTarget <= 0) return [];
    const unit = isImperial ? "lb" : "kg";
    const targetWeight = toDisplayWeight(rawTargetWeight, isImperial);
    const rate = Math.abs(toDisplayRate(ratePerWeek, isImperial)).toFixed(2);
    if (!movingToward || paceState === "offPace") {
      return [{ priority: 2, variant: "warn", icon: "/img/new-icons/bullseye.svg", text: t(tk("goalOffTrack"), { targetWeight, unit }) }];
    }
    // "Behind" is the same verdict the Bodyweight trend card gives (paceState,
    // from buildGoalData) — not a separate weeks-late threshold of its own.
    if (paceState === "behind") {
      const late     = Math.max(1, Math.round(weeksToReach - weeksToTarget));
      const lateUnit = t(tk(late === 1 ? "weekSingular" : "weekPlural"));
      const reqRate  = Math.abs(toDisplayRate(rawReqRate, isImperial)).toFixed(2);
      return [{
        priority: 2, variant: "warn",
        icon: "/img/new-icons/bullseye.svg",
        text: t(tk("goalBehind"), { rate, targetWeight, late, lateUnit, reqRate, extraDeficit, unit }),
      }];
    }
    return [{ priority: 4, variant: "good", icon: "/img/new-icons/bullseye.svg", text: t(tk("goalOnPace"), { targetWeight, rate, unit }) }];
  };

  // Rule 11 — context: the training phase with the lowest intake runs >250 kcal under target;
  // flags recovery risk in hard blocks. Skips when no planner is set.
  const phaseRecovery = (lowestPhase, hasPlanner) => {
    if (!hasPlanner || !lowestPhase) return null;
    if (lowestPhase.deficit == null || lowestPhase.deficit <= 250) return null;
    return {
      priority: 4, variant: "info",
      icon: "/img/new-icons/drop-icon.svg",
      text: t(tk("phaseRecovery"), { phase: lowestPhase.name, intake: formatNumber(lowestPhase.avgIntake), deficit: lowestPhase.deficit }),
    };
  };

  // Rule 12 — context: higher intake during cycle week is expected, not a compliance flag.
  // Only fires when period data is present (consent-gated).
  const cycleWeek = (cycle) => {
    if (!cycle) return null;
    return {
      priority: 5, variant: "info",
      icon: "/img/new-icons/drop-icon.svg",
      text: t(tk("cycleWeek"), { start: fmtUtc(cycle.start), end: fmtUtc(cycle.end) }),
    };
  };

  // Rules 13 & 14 — reinforcement (mutually exclusive, fires at most one):
  //   13: logging ≥85% + adherence ≥90% → strong block.
  //   14: protein hit rate ≥90% → protein well anchored.
  const reinforcement = (stats) => {
    if (stats.loggingRate >= 85 && stats.overallAdh != null && stats.overallAdh >= 90) {
      return [{ priority: 6, variant: "good", icon: "/img/new-icons/check-bold.svg", text: t(tk("strongBlock"), { rate: stats.loggingRate, adh: stats.overallAdh }) }];
    }
    if (stats.proteinHitRate != null && stats.proteinHitRate >= 90) {
      return [{ priority: 6, variant: "good", icon: "/img/new-icons/check-bold.svg", text: t(tk("proteinDialed"), { rate: stats.proteinHitRate }) }];
    }
    return [];
  };

  return { multiPlan, lowLogging, trainingRestGap, weakestMacro, weekdayPattern, intakeVsWeight, goal, phaseRecovery, cycleWeek, reinforcement };
};

/* ---- public API ------------------------------------------------------ */

// goalData: { targetWeight, utcTargetDate, todayUtc } — null when no goal is set (rules 8-10 skip).
// t:          i18next translation function from useTranslation("athlete-portal").
// locale:     BCP 47 tag (i18n.language) for locale-aware number formatting.
// isImperial: coach's unit preference — any weight value/rate in the
//             generated text (intakeVsWeight, goal) follows it.
export const buildInsights = ({
  dayRows,
  stats,
  phaseSpans,
  goalData = null,
  t,
  locale,
  isImperial = false,
}) => {
  const rules       = makeRules(t, locale, isImperial);
  const planChange  = computePlanChangeInfo(dayRows);
  const macroStats  = computeMacroAdhStats(dayRows);
  const wdStats     = computeWeekdayAdhStats(dayRows);
  const lowestPhase = computePhaseIntakeLowest(dayRows, phaseSpans);
  const cycle       = computeCycleWeek(dayRows);
  const avgTarget   = computeAvgTarget(dayRows);
  const hasPlanner  = phaseSpans.length > 0;

  const all = [
    rules.multiPlan(planChange),
    rules.lowLogging(stats),
    rules.trainingRestGap(stats),
    rules.weakestMacro(macroStats),
    rules.weekdayPattern(wdStats),
    ...rules.intakeVsWeight(stats, avgTarget),
    ...rules.goal(goalData),
    rules.phaseRecovery(lowestPhase, hasPlanner),
    rules.cycleWeek(cycle),
    ...rules.reinforcement(stats),
  ].filter(Boolean);

  return all.toSorted((a, b) => a.priority - b.priority).slice(0, 5);
};
